import { useState } from 'react';
import { api, money, useQuery } from '../api.js';
import { useSession } from '../session.js';
import { Switch, Tile } from '../controls.js';
import { Pill, Spinner, useToast } from '../ui.js';
import { GraduationIcon, PeopleIcon } from '../icons.js';


/**
 * School setup: the ways in, the people who came through them, and the things
 * flown. Who may open what is settled by the role a person holds and enforced by
 * the API rather than chosen here, so this page is about what a school actually
 * administers — the codes that let somebody join, the accounts they end up with,
 * and the aircraft on the certificate.
 */
export function Access(): JSX.Element {
  return (
    <div className="access">
      <div className="access__col">
        <JoinCodeCard />
      </div>
      <div className="access__col access__col--wide">
        <PeopleCard />
        <FleetCard />
      </div>
    </div>
  );
}

interface Person {
  id: number; name: string; email: string; role: string; initials: string;
  active: boolean; lastSignedInAt: number | null; self: boolean;
}

const ROLE_WORD: Record<string, string> = {
  management: 'Management', instructor: 'Instructor', dispatch: 'Dispatch', student: 'Student',
};

/**
 * Everybody with an account, and the button that gets one of them back in.
 *
 * This is the school's answer to "I cannot sign in", and it has to work on the
 * deployment with no mail relay — so the temporary password is shown here, once,
 * rather than only sent. It is also the only screen that lists staff and
 * students together, because the person on the phone does not know which of the
 * two rosters they are on.
 */
function PeopleCard(): JSX.Element | null {
  const { role } = useSession();
  const toast = useToast();
  const { data, error, reload } = useQuery<{ mailDelivers: boolean; people: Person[] }>(
    role === 'management' ? '/people' : null,
  );
  const [busy, setBusy] = useState<number | null>(null);
  const [issued, setIssued] = useState<{ id: number; name: string; password: string; emailed: boolean } | null>(null);

  if (role !== 'management' || error) return null;

  const reset = async (person: Person) => {
    setBusy(person.id);
    try {
      const out = await api.post<{ temporaryPassword: string; emailed: boolean }>(`/people/${person.id}/password`);
      setIssued({ id: person.id, name: person.name, password: out.temporaryPassword, emailed: out.emailed });
      toast.ok(
        `${person.name} can sign in again`,
        out.emailed ? 'The temporary password was emailed to them too.' : undefined,
      );
      reload();
    } catch (err) {
      toast.error('Could not reset that password', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(null);
    }
  };

  const setActive = async (person: Person, active: boolean) => {
    setBusy(person.id);
    try {
      await api.post(`/people/${person.id}/active`, { active });
      toast.ok(active ? `${person.name}'s account reopened` : `${person.name}'s account closed`);
      reload();
    } catch (err) {
      toast.error('Could not change that account', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(null);
    }
  };

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.ok('Copied');
    } catch {
      toast.info('Copy it from the screen', value);
    }
  };

  return (
    <div className="card">
      <div className="card__head">
        <span className="card__title">People · {data?.people.length ?? 0}</span>
        {data && !data.mailDelivers && (
          <span className="small muted">No mail relay — a reset has to be read out</span>
        )}
      </div>

      {issued && (
        <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border-2)' }}>
          <div className="small bold">Temporary password for {issued.name}</div>
          <button className="input auth__code" style={{ cursor: 'pointer', marginTop: 8 }}
            onClick={() => copy(issued.password)}
            aria-label={`Temporary password ${issued.password}, copy to clipboard`}>
            {issued.password}
          </button>
          <p className="small muted" style={{ margin: '8px 0 0', textWrap: 'pretty' }}>
            {/* Nothing stores it in readable form, so there is no asking for it
                again — a second reset simply issues another. */}
            Shown once. {issued.emailed
              ? 'It has also been emailed to them.'
              : 'Give it to them yourself.'} They should change it from their
            account menu as soon as they are in.
          </p>
          <button className="btn btn--sm" style={{ marginTop: 8 }} onClick={() => setIssued(null)}>Done</button>
        </div>
      )}

      <div className="table__scroll">
        <table className="table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Role</th><th>Last signed in</th><th /></tr>
          </thead>
          <tbody>
            {!data && <tr><td colSpan={5}><Spinner /></td></tr>}
            {data?.people.map((p) => (
              <tr key={p.id} style={{ opacity: p.active ? 1 : 0.55 }}>
                <td className="bold">
                  {p.name}
                  {p.self && <span className="muted"> · you</span>}
                  {!p.active && <> <Pill tone="gray">Closed</Pill></>}
                </td>
                <td className="muted">{p.email}</td>
                <td>{ROLE_WORD[p.role] ?? p.role}</td>
                <td className="muted">
                  {p.lastSignedInAt ? new Date(p.lastSignedInAt).toLocaleDateString() : 'Never'}
                </td>
                <td>
                  {!p.self && (
                    <span className="row" style={{ gap: 6, justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                      {p.active && (
                        <button className="btn btn--sm" disabled={busy === p.id} onClick={() => reset(p)}>
                          Reset password
                        </button>
                      )}
                      <button className="btn btn--sm" disabled={busy === p.id}
                        onClick={() => setActive(p, !p.active)}>
                        {p.active ? 'Close' : 'Reopen'}
                      </button>
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface FleetAircraft {
  id: number; tail: string; model: string; description: string;
  hobbs: number; rateCents: number; seats: number;
  ifrCapable: boolean; nightCapable: boolean; status: string;
}

/** The aeroplanes the school operates, and the form that adds one. */
function FleetCard(): JSX.Element {
  const { data, reload } = useQuery<{ aircraft: FleetAircraft[]; canManage: boolean }>('/fleet');
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    tail: '', model: '', description: '', hobbs: '0', rate: '', seats: '4',
    ifrCapable: false, nightCapable: true,
  });

  const set = (k: keyof typeof form, v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));

  const add = async () => {
    setBusy(true);
    try {
      await api.post('/fleet', {
        tail: form.tail.trim().toUpperCase(),
        model: form.model.trim(),
        description: form.description.trim() || undefined,
        // Stored in cents, entered in dollars, because nobody quotes a wet rate
        // in cents.
        hobbs: Number(form.hobbs) || 0,
        rateCents: Math.round((Number(form.rate) || 0) * 100),
        seats: Number(form.seats) || 4,
        ifrCapable: form.ifrCapable,
        nightCapable: form.nightCapable,
      });
      toast.ok(`${form.tail.toUpperCase()} added to the fleet`);
      setForm({ tail: '', model: '', description: '', hobbs: '0', rate: '', seats: '4', ifrCapable: false, nightCapable: true });
      setAdding(false);
      reload();
    } catch (err) {
      toast.error('Could not add that aircraft', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  const ready = form.tail.trim().length >= 2 && form.model.trim().length >= 2 && Number(form.rate) > 0;

  return (
    <div className="card">
      <div className="card__head">
        <span className="card__title">Aircraft · {data?.aircraft.length ?? 0}</span>
        {data?.canManage && (
          <button className="btn" onClick={() => setAdding((v) => !v)}>
            {adding ? 'Cancel' : 'Add aircraft'}
          </button>
        )}
      </div>

      {adding && (
        <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border-2)' }}>
          <div className="closeout__grid">
            <label className="field">
              <span>Registration</span>
              <input className="input" value={form.tail} placeholder="N7215F" spellCheck={false}
                onChange={(e) => set('tail', e.target.value.toUpperCase())} />
            </label>
            <label className="field">
              <span>Type</span>
              <input className="input" value={form.model} placeholder="Cessna 172S"
                onChange={(e) => set('model', e.target.value)} />
            </label>
            <label className="field">
              <span>Rate per hour, wet</span>
              <input className="input" inputMode="decimal" value={form.rate} placeholder="185"
                onChange={(e) => set('rate', e.target.value)} />
            </label>
            <label className="field">
              <span>Hobbs now</span>
              <input className="input" inputMode="decimal" value={form.hobbs}
                onChange={(e) => set('hobbs', e.target.value)} />
            </label>
            <label className="field">
              <span>Seats</span>
              <input className="input" inputMode="numeric" value={form.seats}
                onChange={(e) => set('seats', e.target.value)} />
            </label>
            <label className="field" style={{ gridColumn: 'span 2' }}>
              <span>Note (optional)</span>
              <input className="input" value={form.description} placeholder="Garmin G1000 · based on the east ramp"
                onChange={(e) => set('description', e.target.value)} />
            </label>
          </div>
          <div className="row" style={{ gap: 16, marginTop: 12 }}>
            {/* What it is equipped for decides which lessons it can be booked
                for, so it is asked at the point the aeroplane is added. */}
            <label className="row" style={{ gap: 7, flexWrap: 'nowrap' }}>
              <input type="checkbox" checked={form.ifrCapable} onChange={(e) => set('ifrCapable', e.target.checked)} />
              <span className="small">IFR capable</span>
            </label>
            <label className="row" style={{ gap: 7, flexWrap: 'nowrap' }}>
              <input type="checkbox" checked={form.nightCapable} onChange={(e) => set('nightCapable', e.target.checked)} />
              <span className="small">Night capable</span>
            </label>
            <button className="btn btn--primary" style={{ marginLeft: 'auto' }}
              disabled={!ready || busy} onClick={add}>
              {busy ? 'Adding…' : 'Add to the fleet'}
            </button>
          </div>
        </div>
      )}

      <div className="table__scroll">
        <table className="table">
          <thead>
            <tr><th>Registration</th><th>Type</th><th className="num">Hobbs</th><th className="num">Rate</th><th>Equipped</th></tr>
          </thead>
          <tbody>
            {!data && <tr><td colSpan={5}><Spinner /></td></tr>}
            {data?.aircraft.length === 0 && (
              <tr><td colSpan={5} className="empty">No aircraft yet. Add the first one.</td></tr>
            )}
            {data?.aircraft.map((a) => (
              <tr key={a.id}>
                <td className="bold">{a.tail}</td>
                <td>{a.model}<span className="muted">{a.description ? ` · ${a.description}` : ''}</span></td>
                <td className="num">{a.hobbs.toFixed(1)}</td>
                <td className="num">{money(a.rateCents)}</td>
                <td>
                  <span className="row" style={{ gap: 5 }}>
                    {a.ifrCapable && <Pill>IFR</Pill>}
                    {a.nightCapable && <Pill>Night</Pill>}
                    {a.status !== 'airworthy' && <Pill tone="red">{a.status}</Pill>}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface Codes {
  joinCode: string; open: boolean;
  instructorCode: string; instructorOpen: boolean;
}

/**
 * The two codes that let somebody in without being typed onto a roster by hand:
 * one for students, one for instructors. Management can rotate either, which
 * immediately invalidates the old one, or close that door altogether.
 */
function JoinCodeCard(): JSX.Element | null {
  const { role, session } = useSession();
  const toast = useToast();
  const { data, error, reload } = useQuery<Codes>(role === 'management' ? '/org/join-code' : null);
  const [busy, setBusy] = useState(false);

  if (role !== 'management' || error) return null;

  const act = async (kind: 'student' | 'instructor', action: 'regenerate' | 'open' | 'close') => {
    setBusy(true);
    try {
      await api.post('/org/join-code', { kind, action });
      toast.ok(
        action === 'regenerate' ? 'New code issued'
          : action === 'open' ? 'Now accepting' : 'Closed',
        action === 'regenerate' ? 'The previous code no longer works.' : undefined,
      );
      reload();
    } catch (err) {
      toast.error('Could not update that', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  const copy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.ok('Code copied');
    } catch {
      // Clipboard access can be refused; the code is on screen either way.
      toast.info('Copy it from the screen', code);
    }
  };

  // A freelance CFI is not hiring staff, they are bringing on a partner.
  const freelance = session?.org.type === 'freelance';

  const panel = (
    kind: 'student' | 'instructor',
    title: string,
    blurb: string,
    switchLabel: string,
    code: string,
    open: boolean,
    color: 'blue' | 'purple',
    Glyph: (p: { style?: React.CSSProperties }) => JSX.Element,
  ) => (
    <div className="card">
      <div className="card__head">
        <span className="card__title">{title}</span>
        <Pill tone={open ? 'green' : 'gray'}>{open ? 'Open' : 'Closed'}</Pill>
      </div>
      <div className="card__body stack">
        <div className="row" style={{ gap: 12, flexWrap: 'nowrap', alignItems: 'flex-start' }}>
          <Tile color={color} size="lg"><Glyph /></Tile>
          <span className="small muted" style={{ textWrap: 'pretty' }}>{blurb}</span>
        </div>
        <button className="input auth__code" onClick={() => copy(code)} disabled={!open}
          style={{ cursor: 'pointer', opacity: open ? 1 : 0.5 }}
          aria-label={`${title} ${code}, copy to clipboard`}>
          {code}
        </button>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <label className="row" style={{ gap: 9 }}>
            <Switch small checked={open} disabled={busy} label={switchLabel}
              onChange={(next) => act(kind, next ? 'open' : 'close')} />
            <span className="small">{switchLabel}</span>
          </label>
          <button className="btn btn--sm" disabled={busy} onClick={() => act(kind, 'regenerate')}>
            New code
          </button>
        </div>
      </div>
    </div>
  );

  if (!data) return <div className="card"><Spinner label="Loading…" /></div>;

  return (
    <>
      {panel('student', 'Student join code',
        'A student enters this to join your roster and request lessons.',
        'Accepting new students', data.joinCode, data.open, 'blue', GraduationIcon)}

      {panel('instructor',
        freelance ? 'Partner instructor code' : 'Instructor join code',
        freelance
          ? 'A CFI enters this to fly alongside you. They bring their own students and their own logbook; the fleet, the rates and the invoices stay yours.'
          : 'A CFI enters this to join your instructor roster. They give their certificate number and expiry as they sign up, and you are told the moment they do.',
        freelance ? 'Accepting partners' : 'Accepting new instructors',
        data.instructorCode, data.instructorOpen, 'purple', PeopleIcon)}
    </>
  );
}
